import { Formik, Form } from "formik";
import { useMemo, useState } from "react";
import { SimpleSidebar } from "@volca/ui/components/ui/simple-sidebar";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@volca/ui/components/ui/tabs";
import { getAddressFromUpdateAddressParams } from "@/core/utils/address";
import { ReferralActivityList } from "@/referrals/common/referral-activity-list";
import { SidebarHeader } from "@/referrals/common/sidebar-header";
import type {
  IReferral,
  ReferralColumn,
  ReferralUpdateParams,
} from "@/referrals/referral";
import { ReferralActionButton } from "./referral-action-button";
import { ReferralDetailsForm } from "./referral-details-form";
import { FormValues, validationSchema } from "./schema";

interface KanbanSidebarProps {
  referral: IReferral | null;
  onClose: () => void;
  onUpdate: (values: Partial<ReferralUpdateParams>) => void;
  onNextColumn: (column: ReferralColumn, referral: IReferral) => void;
  onLost: () => void;
  onPayout: () => void;
  onDelete: () => void;
}

export const KanbanSidebar = ({
  referral,
  onClose,
  onUpdate,
  onNextColumn,
  onLost,
  onPayout,
  onDelete,
}: KanbanSidebarProps) => {
  const [activeTab, setActiveTab] = useState("details");

  const initialValues = useMemo<FormValues>(
    () => ({
      service_needed: referral?.service_needed || "",
      service_titan_job_id: referral?.service_titan_job_id?.toString() || "",
      address: referral?.address || {
        line_one: "",
        line_two: "",
        city: "",
        state: "",
        zip_code: "",
      },
      additional_info: referral?.additional_info || "",
      appointment_date: referral?.appointment_date || null,
    }),
    [referral],
  );

  return (
    <SimpleSidebar data-cy="referral-kanban-sidebar">
      {referral && (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values: FormValues, { resetForm }) => {
            const updateParams: Partial<ReferralUpdateParams> = {
              ...values,
              address: getAddressFromUpdateAddressParams(values.address),
              service_titan_job_id: values.service_titan_job_id
                ? parseInt(values.service_titan_job_id)
                : null,
              appointment_date: values.appointment_date,
            };

            onUpdate(updateParams);
            resetForm({ values });
          }}
          enableReinitialize
          className="h-full"
        >
          {() => (
            <Stack align="start" gap={0} className="h-full w-full">
              <Form className="flex h-full w-full flex-col">
                <SidebarHeader
                  referral={referral}
                  actionButton={
                    <ReferralActionButton
                      referral={referral}
                      onNextColumn={onNextColumn}
                      onPayout={onPayout}
                      className="flex-1"
                    />
                  }
                  onClose={onClose}
                  onPayout={onPayout}
                  onLost={onLost}
                  onMoveToPaid={() => onUpdate({ status: "PAID" })}
                  onDelete={onDelete}
                />
                <Tabs
                  value={activeTab}
                  onValueChange={setActiveTab}
                  className="flex flex-1 flex-col gap-0 overflow-hidden pb-6 pl-6"
                >
                  <div className="border-b">
                    <TabsList variant="line" className="w-full">
                      <TabsTrigger
                        value="details"
                        className="flex-1"
                        onClick={() => setActiveTab("details")}
                      >
                        Details
                      </TabsTrigger>
                      <TabsTrigger
                        value="history"
                        className="flex-1"
                        onClick={() => setActiveTab("history")}
                      >
                        History
                      </TabsTrigger>
                    </TabsList>
                  </div>
                  <TabsContent value="details" className="min-h-0">
                    <div className="h-full w-full overflow-y-auto">
                      <ReferralDetailsForm
                        referral={referral}
                        initialValues={initialValues}
                      />
                    </div>
                  </TabsContent>
                  <TabsContent value="history" className="min-h-0">
                    <div className="h-full overflow-y-auto">
                      <ReferralActivityList referral={referral} />
                    </div>
                  </TabsContent>
                </Tabs>
              </Form>
            </Stack>
          )}
        </Formik>
      )}
    </SimpleSidebar>
  );
};
